import React, { useState, useEffect } from 'react';
import { colors, typography, spacing } from '../../../theme';
import { getUserCheckIns } from '../../../services/checkInService';
import { getCoachNotes } from '../../../services/coachNotesService';
import BodyStatsSection from '../../bodystats/BodyStatsSection';
import ProgressPhotosCompare from './ProgressPhotosCompare';
import CheckInHistoryList from './CheckInHistoryList';
import CheckInDetail from './CheckInDetail';

/**
 * Check-Ins tab — replaces the old "Profile" tab. Represents body-weight
 * progress, progress photos, and check-in history (each with an optional
 * Coach Review, migration 052) — explicitly not a second messaging inbox;
 * ordinary coach/client chat lives in the existing Messages feature.
 * props: { userId }
 */
const CheckInsTab = ({ userId, onStartWorkout }) => {
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [checkIns, setCheckIns] = useState([]);
  const [coachNotes, setCoachNotes] = useState([]);
  const [view, setView] = useState('photos'); // 'photos' | 'history'
  const [selectedCheckIn, setSelectedCheckIn] = useState(null);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setLoadFailed(false);
      try {
        // Weight, body fat and muscle are Body stats, which loads its own (plan 008)
        const [checkInData, coachNotesData] = await Promise.all([
          getUserCheckIns(userId),
          getCoachNotes(userId).catch(() => []),
        ]);
        if (cancelled) return;
        setCheckIns(checkInData || []);
        setCoachNotes(coachNotesData || []);
      } catch (err) {
        console.error('[CheckInsTab] load failed:', err);
        if (!cancelled) setLoadFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [userId]);

  if (loading) {
    return <div style={{ ...typography.body, color: colors.textMuted, padding: spacing.xl }}>Loading…</div>;
  }

  // A failed load must not read as "nothing yet"
  if (loadFailed) {
    return (
      <div style={{ ...typography.body, color: colors.textMuted, padding: spacing.xl, textAlign: 'center' }}>
        Could not load your check-ins. Check your connection and open this tab again.
      </div>
    );
  }

  // No check-in yet: Body stats (which explains itself when empty), then what check-ins are for,
  // instead of a heading over a blank page (15 October 2026)
  if (checkIns.length === 0) {
    return (
      <div>
      <BodyStatsSection clientId={userId} viewer="client" />
      <div style={{
        backgroundColor: colors.surface1, border: `1px solid ${colors.border}`, borderRadius: '14px',
        padding: '28px 20px', textAlign: 'center', marginTop: spacing.lg,
      }}>
        <div style={{ fontSize: '32px', marginBottom: '12px' }} aria-hidden="true">📋</div>
        <h2 style={{ ...typography.sectionHeading, color: colors.text, margin: '0 0 8px' }}>
          Your first check-in comes after about four weeks of training
        </h2>
        <p style={{ ...typography.body, color: colors.textMuted, margin: '0 0 20px', lineHeight: 1.5 }}>
          You'll add your weight and a few progress photos. Your coach reviews each check-in, and your
          weight trend, photos and your coach's feedback collect here.
        </p>
        {onStartWorkout && (
          <button type="button" onClick={onStartWorkout} style={{
            padding: '12px 20px', borderRadius: '8px', border: 'none', backgroundColor: colors.gold,
            color: colors.bg, fontSize: '15px', fontWeight: 700, cursor: 'pointer',
          }}>
            Go to today's workout
          </button>
        )}
      </div>
      </div>
    );
  }

  const coachReviewFor = (checkIn) =>
    coachNotes.find(n => n.check_in_id === checkIn.id) || null;

  // The photo comparison hides itself when no check-in has photos, and with it the only way into the
  // history: then the history is shown straight away (Codex's task 017 review, 21 October 2026)
  const hasPhotos = checkIns.some(c => c.photo_urls?.length > 0);
  const showing = hasPhotos ? view : 'history';

  return (
    <div>
      <div style={{ marginBottom: spacing.lg }}>
        <div style={{ ...typography.screenTitle, color: colors.text }}>Check-Ins</div>
      </div>

      <BodyStatsSection clientId={userId} viewer="client" />

      {showing === 'photos' && (
        <ProgressPhotosCompare checkIns={checkIns} onViewHistory={() => setView('history')} />
      )}

      {showing === 'history' && (
        <CheckInHistoryList checkIns={checkIns} onSelectCheckIn={setSelectedCheckIn} />
      )}

      {showing === 'history' && hasPhotos && (
        <button type="button" onClick={() => setView('photos')} style={{
          display: 'block', width: '100%', minHeight: '44px', background: 'none', border: 'none',
          color: colors.textMuted, fontSize: '13px', textDecoration: 'underline', cursor: 'pointer', textAlign: 'center',
        }}>
          Back to progress photos
        </button>
      )}

      {selectedCheckIn && (
        <CheckInDetail
          checkIn={selectedCheckIn}
          coachReview={coachReviewFor(selectedCheckIn)}
          onClose={() => setSelectedCheckIn(null)}
        />
      )}
    </div>
  );
};

export default CheckInsTab;
